import React, { useCallback, useEffect, useRef, useState } from 'react';
import Panel from 'Components/Panel/Panel';
import Button from 'Components/Button/Button';
import './OptionsPanel.css';

const OptionsPanel = ({
    options,
    zIndex,
    selectDefault,
    defaultValue,
    mainPanelStyle,
    titleOptionsStyle,
    attributesToValidateSelectedOption,
    goPreviousTab,
    onSelectOption,
    styleOptionSelected
}) => {
    const [selectedOption, setSelectedOption] = useState(0);
    const tabsRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Indica se há abas escondidas à esquerda/direita da faixa
    const updateScrollIndicators = useCallback(() => {
        const tabs = tabsRef.current;
        if (!tabs) return;
        const maxScroll = tabs.scrollWidth - tabs.clientWidth;
        setCanScrollLeft(tabs.scrollLeft > 1);
        setCanScrollRight(tabs.scrollLeft < maxScroll - 1);
    }, []);

    useEffect(() => {
        const tabs = tabsRef.current;
        if (!tabs) return;

        updateScrollIndicators();
        tabs.addEventListener('scroll', updateScrollIndicators, { passive: true });
        const observer = new ResizeObserver(updateScrollIndicators);
        observer.observe(tabs);

        return () => {
            tabs.removeEventListener('scroll', updateScrollIndicators);
            observer.disconnect();
        };
    }, [updateScrollIndicators]);

    useEffect(() => {
        updateScrollIndicators();
    }, [options, updateScrollIndicators]);

    // Mantém a aba selecionada visível (rola só a faixa, nunca a página)
    useEffect(() => {
        const tabs = tabsRef.current;
        const tab = tabs?.children[selectedOption];
        if (!tab) return;

        const arrowSpace = 36;
        // .options-tabs é position: relative, então offsetLeft é relativo à faixa
        const tabLeft = tab.offsetLeft;
        const tabRight = tabLeft + tab.offsetWidth;
        if (tabLeft < tabs.scrollLeft + arrowSpace) {
            tabs.scrollTo({ left: Math.max(0, tabLeft - arrowSpace), behavior: 'smooth' });
        } else if (tabRight > tabs.scrollLeft + tabs.clientWidth - arrowSpace) {
            tabs.scrollTo({ left: tabRight - tabs.clientWidth + arrowSpace, behavior: 'smooth' });
        }
    }, [selectedOption]);

    function scrollTabs(direction) {
        const tabs = tabsRef.current;
        if (!tabs) return;
        tabs.scrollBy({ left: direction * tabs.clientWidth * 0.7, behavior: 'smooth' });
    }

    function getScrollArrow(direction) {
        const isLeft = direction < 0;
        return (
            <button
                type='button'
                className={`options-tabs-arrow ${isLeft ? 'left' : 'right'}`}
                onClick={() => scrollTabs(direction)}
                aria-label={isLeft ? 'Ver abas anteriores' : 'Ver mais abas'}
                title={isLeft ? 'Ver abas anteriores' : 'Ver mais abas'}
                tabIndex={-1}
            >
                <svg width='16' height='16' viewBox='0 0 24 24' aria-hidden='true'>
                    <path
                        d={isLeft ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </svg>
            </button>
        );
    }

    useEffect(() => {
        if (selectDefault) {
            setSelectedOption(defaultValue);
            onSelectOption?.(defaultValue);
        }
    }, [selectDefault]);


    useEffect(() => {
        function validatePreviousTab(selectTab) {
            let previousTab;
            if (options[selectTab] && options[selectTab].readOnly && goPreviousTab && selectTab > 0) {
                previousTab = selectTab - 1;
                setSelectedOption(previousTab);
                onSelectOption?.(previousTab);
            }

            if (previousTab > 0 && options[previousTab] && options[previousTab].readOnly) {
                validatePreviousTab(previousTab)
            }
        }

        validatePreviousTab(selectedOption)
    }, [attributesToValidateSelectedOption]);

    const selectedOptionStyle = {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: `calc(100% - 30px)`,
    };

    const titleOptionsCss = {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '30px',
        ...titleOptionsStyle
    };

    function onUpdateSelectedOption(index, readOnly, option) {
        if (index === null || !options[index] || !options[index].component || readOnly) {
            return;
        }

        if (option.sequence || option.setSequenceClicked) {
            const response = option.setSequenceClicked(option.sequence)
            if (response) {
                onSelectOption?.(index);
                setSelectedOption(index)
            }
        } else {
            onSelectOption?.(index);
            setSelectedOption(index)
        }

    }

    return (
        <Panel style={{
            width: '100%',
            height: '100%',
            flexDirection: 'column',
            zIndex: zIndex,
            ...mainPanelStyle
        }}>
            <div className='options-tabs-bar'>
                <Panel ref={tabsRef} style={titleOptionsCss} className={'options-tabs'}>
                    {options.map((option, index) => (
                        <Button
                            key={index}
                            clickMethod={() => onUpdateSelectedOption(index, option.readOnly, option)}
                            buttonName={option ? option.label : null}
                            className={selectedOption === index ? 'options-tab is-active' : 'options-tab'}
                            title={typeof option?.label === 'string' ? option.label : undefined}
                            style={{
                                fontWeight: selectedOption === index ? 'bold' : 'normal',
                                color: option.colorCode ? option.colorCode : 'black',
                                padding: '5px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                ...(selectedOption === index && styleOptionSelected)
                            }}
                        />
                    ))}
                </Panel>
                {canScrollLeft ? getScrollArrow(-1) : null}
                {canScrollRight ? getScrollArrow(1) : null}
            </div>
            <Panel style={selectedOptionStyle} className={'options-tab-content'}>
                {selectedOption !== null && options[selectedOption] && options[selectedOption].component}
            </Panel>
        </Panel>
    );
};

export default OptionsPanel;
